import React, { useState, useEffect, useRef } from 'react'
import { Button } from '../../../ui/Button'

interface NewLeaseQuoteContentProps {
  pdfUrl?: string
  onGoBack?: () => void
  onConfirmAndContinue?: () => void
}

const NewLeaseQuoteContent: React.FC<NewLeaseQuoteContentProps> = ({
  pdfUrl,
  onGoBack,
  onConfirmAndContinue,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [autoRefreshCount, setAutoRefreshCount] = useState(0)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getGoogleViewerUrl = (url: string) => {
    return `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true&cachebuster=${refreshKey}`
  }

  const clearLoadingTimeout = () => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
      loadingTimeoutRef.current = null
    }
  }

  const startLoadingTimeout = () => {
    clearLoadingTimeout()
    loadingTimeoutRef.current = setTimeout(() => {
      // Si después de 3 segundos sigue cargando, hacer auto-refresh
      if (isLoading && autoRefreshCount < 3) {
        console.log(
          `Auto-refresh triggered after 3 seconds (attempt ${autoRefreshCount + 1})`
        )
        setAutoRefreshCount(prev => prev + 1)
        refreshViewer()
      } else if (isLoading && autoRefreshCount >= 3) {
        // Si ya se intentó 3 veces, mostrar error
        setIsLoading(false)
        setHasError(true)
      }
    }, 3000)
  }

  const handleIframeLoad = () => {
    clearLoadingTimeout()
    setIsLoading(false)
    setHasError(false)

    // Verificar si el iframe cargó correctamente después de un delay
    setTimeout(() => {
      checkIframeContent()
    }, 1000)
  }

  const checkIframeContent = () => {
    try {
      const iframe = iframeRef.current
      if (iframe && iframe.contentDocument) {
        const content = iframe.contentDocument.body?.innerText || ''
        // Google Docs viewer muestra mensajes de error específicos
        if (
          content.includes('Sorry') ||
          content.includes('temporarily unavailable') ||
          content.includes('Error') ||
          content.includes('not available') ||
          content.trim() === ''
        ) {
          handleIframeError()
        }
      }
    } catch (error) {
      // Error de CORS es normal, pero si hay otros errores...
      console.log('Cannot access iframe content (normal CORS behavior)', error)
    }
  }

  const handleIframeError = () => {
    clearLoadingTimeout()
    setIsLoading(false)
    setHasError(true)

    // Auto-refresh hasta 3 veces con un delay
    if (autoRefreshCount < 3) {
      setTimeout(() => {
        setAutoRefreshCount(prev => prev + 1)
        refreshViewer()
      }, 1000)
    }
  }

  const refreshViewer = () => {
    clearLoadingTimeout()
    setIsLoading(true)
    setHasError(false)
    setRefreshKey(prev => prev + 1)
    // Iniciar el timeout para este nuevo intento
    startLoadingTimeout()
  }

  const manualRefresh = () => {
    setAutoRefreshCount(0) // Reset auto refresh counter
    refreshViewer()
  }

  // Reset cuando cambia la URL del PDF
  useEffect(() => {
    if (pdfUrl) {
      clearLoadingTimeout()
      setIsLoading(true)
      setHasError(false)
      setRefreshKey(0)
      setAutoRefreshCount(0)
      startLoadingTimeout()
    }

    // Cleanup timeout on unmount
    return () => {
      clearLoadingTimeout()
    }
  }, [pdfUrl])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearLoadingTimeout()
    }
  }, [])

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Tu cotización está lista
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
          Hemos revisado tu información y generado una propuesta con las
          condiciones de arrendamiento. Por favor, revísala cuidadosamente y
          elige cómo deseas continuar.
        </p>
      </div>

      {/* PDF Viewer Section */}
      <div
        className="bg-[#D9D9D9] rounded-lg mb-8 relative overflow-hidden"
        style={{ height: '500px' }}
      >
        {pdfUrl ? (
          <>
            {/* Loading Overlay */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <div className="text-gray-500">
                    Cargando PDF...
                    {autoRefreshCount > 0 &&
                      ` (Intento ${autoRefreshCount + 1}/4)`}
                  </div>
                  {autoRefreshCount > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Reintentando automáticamente...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error Overlay */}
            {hasError && !isLoading && autoRefreshCount >= 3 && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center">
                  <div className="text-red-500 text-lg font-medium mb-2">
                    Error al cargar el PDF
                  </div>
                  <div className="text-gray-400 text-sm mb-4">
                    Google Docs viewer no responde después de {autoRefreshCount}{' '}
                    intentos
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={manualRefresh}
                      className="text-blue-500 border-blue-500"
                    >
                      Reintentar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(pdfUrl, '_blank')}
                      className="text-green-500 border-green-500"
                    >
                      Abrir en nueva pestaña
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* PDF Iframe */}
            <iframe
              ref={iframeRef}
              key={refreshKey} // Force re-render when refreshKey changes
              src={getGoogleViewerUrl(pdfUrl)}
              className="w-full h-full border-0"
              title="PDF Viewer"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-scripts allow-same-origin"
            />

            {/* Refresh Button - Moved to top-left */}
            {!isLoading && !hasError && (
              <div className="absolute top-2 left-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={manualRefresh}
                  className="bg-white/90 text-xs h-7 px-3 text-gray-600 border-gray-300 shadow-sm hover:bg-white"
                  title="Recargar PDF"
                >
                  🔄 Refresh
                </Button>
              </div>
            )}

            {/* Manual Refresh during auto-refresh phase */}
            {isLoading && autoRefreshCount > 0 && autoRefreshCount < 3 && (
              <div className="absolute bottom-2 left-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={manualRefresh}
                  className="bg-white/90 text-xs h-7 px-3 text-blue-600 border-blue-300 shadow-sm hover:bg-white"
                  title="Recargar manualmente"
                >
                  Refresh manual
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-gray-500 text-lg font-medium mb-2">
                AQUÍ SE MUESTRA EL PDF DE LA COTIZACIÓN
              </div>
              <div className="text-gray-400 text-sm">
                No hay PDF disponible para mostrar
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-4">
        <Button
          variant="outline"
          size="default"
          onClick={onGoBack}
          className="px-8 py-3 text-blue-500 border-blue-500 hover:bg-blue-50"
        >
          Regresar
        </Button>

        <Button
          size="default"
          onClick={onConfirmAndContinue}
          className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white"
        >
          Confirmar y continuar
        </Button>
      </div>
    </div>
  )
}

export default NewLeaseQuoteContent
